import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Check, Car, Heart, Factory, Battery, Plane, Server, Radio, Monitor } from 'lucide-react';
import { t } from '../utils/translation';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import PageHeader from '../components/PageHeader';
import SectionSplitter from '../components/SectionSplitter';

const Industries = ({ theme, language }) => {
  const industryData = [
    { 
      nameKey: 'automotive', 
      icon: <Car className="w-8 h-8 text-red-600" />, 
      descKey: 'vehicleSystems',
      detailsKeys: [
        'engineControl',
        'infotainment',
        'safetyADAS',
        'electricVehicle',
        'powertrain'
      ],
      applicationsKey: 'automotiveApplications'
    },
    { 
      nameKey: 'medical', 
      icon: <Heart className="w-8 h-8 text-red-600" />, 
      descKey: 'highReliability',
      detailsKeys: [
        'patientMonitoring',
        'diagnosticEquipment',
        'surgicalInstruments',
        'imagingSystems',
        'laboratoryEquipment'
      ],
      applicationsKey: 'medicalApplications'
    },
    { 
      nameKey: 'industrial', 
      icon: <Factory className="w-8 h-8 text-red-600" />, 
      descKey: 'controlMonitoring',
      detailsKeys: [
        'plcSystems',
        'robotics',
        'sensorsActuators',
        'industrialNetworking',
        'processControl'
      ],
      applicationsKey: 'industrialApplications'
    },
    { 
      nameKey: 'energyStorage', 
      icon: <Battery className="w-8 h-8 text-red-600" />, 
      descKey: 'batteryManagement',
      detailsKeys: [
        'batteryPacks',
        'energyStorageCabinets',
        'powerDistribution',
        'solarSystems',
        'upsSystems'
      ],
      applicationsKey: 'energyApplications'
    },
    { 
      nameKey: 'aerospace', 
      icon: <Plane className="w-8 h-8 text-red-600" />, 
      descKey: 'aviationDefense',
      detailsKeys: [
        'avionics',
        'flightControl',
        'communicationSystems',
        'navigationEquipment',
        'defenseElectronics'
      ],
      applicationsKey: 'aerospaceApplications'
    },
    { 
      nameKey: 'serverData', 
      icon: <Server className="w-8 h-8 text-red-600" />, 
      descKey: 'highSpeed',
      detailsKeys: [
        'dataCenters',
        'serverSystems',
        'storageArrays',
        'networkingEquipment',
        'cloudInfrastructure'
      ],
      applicationsKey: 'serverApplications'
    },
    { 
      nameKey: 'telecommunications', 
      icon: <Radio className="w-8 h-8 text-red-600" />, 
      descKey: 'communicationInfrastructure',
      detailsKeys: [
        'fiveG',
        'networkEquipment',
        'fiberOptic',
        'wirelessSystems',
        'satelliteCommunications'
      ],
      applicationsKey: 'telecomApplications'
    },
    { 
      nameKey: 'consumer', 
      icon: <Monitor className="w-8 h-8 text-red-600" />, 
      descKey: 'personalDevices',
      detailsKeys: [
        'smartphones',
        'tablets',
        'wearables',
        'smartHome',
        'audioEquipment'
      ],
      applicationsKey: 'consumerApplications'
    }
  ];

  const translatedIndustries = industryData.map(industry => ({
    name: t(industry.nameKey, language),
    icon: industry.icon,
    desc: t(industry.descKey, language),
    details: industry.detailsKeys.map(key => t(key, language)),
    applications: t(industry.applicationsKey, language)
  }));

  // Industries section with scroll animation
  const [industriesRef, industriesVisible] = useScrollAnimation(0.1);

  const industriesWeServeText = t('industriesWeServe', language);
  const industriesDescriptionText = t('industriesDescription', language);
  const connectorSolutionsText = t('connectorSolutions', language);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
      <Helmet>
        <title>{industriesWeServeText} - Qingshun Electronics</title>
        <meta name="description" content={industriesDescriptionText} />
        <meta name="keywords" content="connector solutions, automotive connectors, medical connectors, industrial connectors, energy storage connectors, aerospace connectors, data center connectors" />
        <meta name="author" content="Qingshun Electronics Technology Co., Ltd." />
        <link rel="canonical" href="https://qsconn.com/industries" />
        <meta name="description" content={industriesDescriptionText} />
        <meta name="keywords" content="connector solutions, automotive connectors, medical connectors, industrial connectors, energy storage connectors, aerospace connectors, data center connectors, telecommunications connectors, consumer electronics connectors, custom connectors by industry" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://qsconn.com/industries" />
        <meta property="og:title" content={`${industriesWeServeText} | Automotive, Medical, Industrial & Data Center Connectors - Qingshun Electronics`} />
        <meta property="og:description" content={industriesDescriptionText} />
        <meta property="og:image" content="https://qsconn.com/logo512.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://qsconn.com/industries" />
        <meta property="twitter:title" content={`${industriesWeServeText} | Qingshun Electronics - Connector Manufacturer in China`} />
        <meta property="twitter:description" content={industriesDescriptionText} />
        <meta property="twitter:image" content="https://qsconn.com/logo512.png" />
        <meta property="twitter:image:alt" content={`${industriesWeServeText} - Qingshun Electronics`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            "name": industriesWeServeText,
            "description": connectorSolutionsText,
            "url": "https://qsconn.com/industries",
            "itemListElement": translatedIndustries.map((industry, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": "Thing",
                "name": industry.name,
                "description": industry.desc
              }
            }))
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://qsconn.com/"},
              {"@type": "ListItem", "position": 2, "name": industriesWeServeText, "item": "https://qsconn.com/industries"}
            ]
          })}
        </script>
      </Helmet>

      <PageHeader
        theme={theme}
        badge={t('industries', language)}
        title={t('industriesWeServe', language)}
        subtitle={t('providingReliable', language)}
      />

      {/* Industries Grid */}
      <section 
        ref={industriesRef}
        className="py-20 px-4"
      >
        <div className="max-w-7xl mx-auto">
          <div className="space-y-12">
            {translatedIndustries.map((industry, idx) => (
              <div 
                key={idx} 
                className={`rounded-3xl p-8 md:p-12 shadow-lg transform hover:-translate-y-1 transition-all duration-300 hover:bg-gradient-to-br ${theme === 'dark' ? 'bg-black border-2 border-gray-800 hover:border-red-600 from-red-900/10 to-red-900/5' : 'bg-white border-2 border-gray-300 hover:border-red-600 from-red-100 to-red-50'}`}
                style={{
                  animation: industriesVisible ? `fadeInUp 0.6s ease-out ${idx * 0.1}s both` : 'none',
                }}
              >
                <div className="grid md:grid-cols-3 gap-8">
                  <div className="md:col-span-1">
                    <div className="text-7xl mb-4 transform hover:scale-110 transition duration-300">{industry.icon}</div>
                    <h2 className="text-3xl font-bold text-red-400 mb-2 transition-colors duration-300">{industry.name}</h2>
                    <p className={`text-lg mb-6 transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}>{industry.desc}</p>
                    <div className={`rounded-xl p-4 transition-all duration-300 ${theme === 'dark' ? 'bg-black border-2 border-gray-800' : 'bg-white border-2 border-gray-300'}`}>
                      <p className={`text-sm mb-2 transition-colors duration-300 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-700'}`}>{t('keyApplications', language)}:</p>
                      <p className={`text-sm transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}>{industry.applications}</p>
                    </div>
                  </div>
                  
                  <div className="md:col-span-2">
                    <h3 className="text-xl font-bold mb-4 text-red-400 transition-colors duration-300">{t('solutionsWeProvide', language)}:</h3>
                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                      {industry.details.map((detail, detailIdx) => (
                        <div key={detailIdx} className={`rounded-xl p-4 transform hover:scale-105 transition-all duration-300 ${theme === 'dark' ? 'bg-black border-2 border-gray-800 hover:border-red-600' : 'bg-white border-2 border-gray-300 hover:border-red-600'}`}>
                          <Check className={`w-5 h-5 mr-3 flex-shrink-0 mt-0.5 ${theme === 'dark' ? 'text-red-600' : 'text-red-600'}`} />
                          <span className={`transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-800'}`}>{detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SectionSplitter theme={theme} variant="wave" />

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className={`rounded-3xl p-12 shadow-lg relative overflow-hidden transition-all duration-300 ${theme === 'dark' ? 'bg-black border-2 border-red-900/50' : 'bg-white border-2 border-red-200'}`}>
            <h2 className={`text-4xl md:text-5xl font-bold mb-6 transition-colors duration-300 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
              {t('needSolutionsSpecificIndustry', language)}
            </h2>
            <p className={`text-xl mb-8 transition-colors duration-300 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              {t('engineeringTeam', language)}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contact" 
                className="bg-gradient-to-r from-red-600 to-red-700 px-8 py-4 rounded-xl text-lg font-semibold text-white hover:from-red-700 hover:to-red-800 transition-all shadow-lg shadow-red-500/30 transform hover:scale-105 duration-300"
              >
                {t('talkToExpert', language)}
              </a>
              <a 
                href="/services" 
                className={`px-8 py-4 rounded-xl text-lg font-semibold border-2 transition-colors duration-300 ${theme === 'dark' ? 'border-red-600 text-red-400 hover:bg-red-900/30' : 'border-red-600 text-red-600 hover:bg-red-100'}`}
              >
                {t('viewAllServices', language)}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Industries;